import { OtsBadge } from "../components/OtsBadge";
import { OtsCheckbox } from "../components/OtsCheckbox";
import { DescriptionItem, DescriptionList } from "../components/DescriptionList";
import { OtsTooltip } from "../components/OtsTooltip";
import { ActivityAttachmentsBadge } from "./ActivityAttachmentsBadge";
import { AuditScoreRing } from "../audit/AuditScoreRing";
import { ActivityEntryPreview } from "./ActivityEntryPreview";
import { OtsFlaggedValue } from "../components/OtsFlaggedValue";
import { OtsStepDots, type OtsStepDotTone } from "../components/OtsStepDots";
import { OtsTransportCategoryIcon } from "../components/OtsTransportCategoryIcon";
import { ActivityMarkerIcons } from "./ActivityMarkerIcons";
import { ActivityStatusIcon } from "./ActivityStatusIcon";
import { ActivityStopCells } from "./ActivityStopCells";
import {
  formatActivityDuration,
  formatActivityMoney,
  formatActivityQuantity,
  formatActivityTime,
} from "./driver-activity-format";
import {
  activityCellClass,
  activityGroupStartClass,
  activityMutedClass,
  activityRowClass,
} from "./driver-activity-layout";
import { activityKindTheme, activityRailClass, activityTintClass } from "./driver-activity-theme";
import {
  type ActivityDurationMode,
  type ActivityEntry,
  type ActivityValidationStep,
  activityIdText,
  isActivityTicket,
} from "./driver-activity-view-model";
import { type DriverActivityRowSettings, useDriverActivityRowSettings } from "./DriverActivityRowContext";

/** A row of the grid. Shared settings come from the list (`DriverActivityRowSettings`); any given here win. */
export type ActivityEntryRowProps = Omit<DriverActivityRowSettings, "selected" | "activeKey" | "rowIdPrefix" | "onEntryContextMenu"> & {
  entry: ActivityEntry;
  isSelected?: boolean;
  /** The row whose details are open in the inspector. */
  isActive?: boolean;
  /** Opens a ticket's tools menu where it was right-clicked. */
  onContextMenu?: (entry: ActivityEntry, point: { x: number; y: number }) => void;
  /** Element id, so a parent can scroll the row into view. */
  id?: string;
};

/** Stop facts left out of the location cells as noise; the inspector still lists them. */
const HIDDEN_STOP_MARKERS = new Set(["manual"]);
const stopMarkersFor = (markers: ActivityEntry["pickupMarkers"]) => markers.filter((marker) => !HIDDEN_STOP_MARKERS.has(marker.id));

/** Marker icons that fit beside the attachments badge on the last column's second line. */
const MARKERS_PER_LINE = 5;

// The WaterGo grid's validation colors: dispatch gray, field teal, business green.
const validationTones: Record<ActivityValidationStep["id"], OtsStepDotTone> = {
  dispatch: "neutral",
  field: "blue",
  admin: "indigo",
  business: "success",
};

const durationFacts: [ActivityDurationMode, string][] = [
  ["turnaround", "Turnaround"], ["onSite", "On site"], ["travel", "Travel"], ["billable", "Billable"],
];

const timeRange = (entry: ActivityEntry, timeZone: string | undefined, openText: string) =>
  `${formatActivityTime(entry.time, timeZone)} – ${entry.endTime != null ? formatActivityTime(entry.endTime, timeZone) : openText}`;

/** The one-line summary of a row that is not a ticket. */
const SpanSummary = ({ entry, timeZone }: { entry: ActivityEntry; timeZone?: string }) => {
  const { geofenceEvent: event, dispatch, serviceWork: work, containerVolumeDrop: drop } = entry;
  const theme = activityKindTheme[entry.kind];
  const strong = "truncate font-semibold text-[var(--ots-text)]";

  return <>
    <OtsBadge tone={theme.badgeTone} size="compact">{theme.label}</OtsBadge>
    {event && <>
      <span className={strong}>{event.siteName ?? "Unknown site"}{(event.parentSiteId || event.geofenceSiteGroupId) && <span title="Recorded against a child site or site group">*</span>}</span>
      {[event.siteCompanyName, event.geofenceSiteGroupName].filter(Boolean).map((text) => <span key={text} className="truncate">{text}</span>)}
      <span>In {timeRange(entry, timeZone, "still on site")}</span>
    </>}
    {dispatch && <>
      <span>{formatActivityTime(entry.time, timeZone)}</span>
      <span className={strong}>{dispatch.pickupSiteName ?? "Any pickup"} → {dispatch.deliverySiteName ?? "Any delivery"}</span>
      {dispatch.waterTypeName && <span className="flex items-center gap-1"><OtsTransportCategoryIcon category={dispatch.category} className="size-3"/>{dispatch.waterTypeName}</span>}
      {dispatch.quantityDecimal != null && <span>{formatActivityQuantity(dispatch.quantityDecimal, dispatch.quantityUnitOfMeasure)}</span>}
      {dispatch.status && <OtsBadge size="compact">{dispatch.status.toLowerCase()}</OtsBadge>}
      <ActivityMarkerIcons markers={entry.markers}/>
    </>}
    {work && <>
      <span className={strong}>{[work.serviceTypeName, work.siteName].filter(Boolean).join(" · ") || "Site work"}</span>
      <span>{timeRange(entry, timeZone, "in progress")}</span>
      {work.billableHours != null && <span>{formatActivityQuantity(work.billableHours)} billable h</span>}
    </>}
    {drop && <>
      <span className={strong}>{[drop.siteName, drop.containerName].filter(Boolean).join(" · ") || "Unknown tank"}</span>
      <span>{timeRange(entry, timeZone, "")}</span>
      <span className="font-semibold text-[var(--ots-on-blue-container)]">−{formatActivityQuantity(drop.volume, drop.unitOfMeasure)}</span>
      <span>No ticket found for this drop</span>
    </>}
  </>;
};

/** One line of a driver's day: a load, a one-sided ticket, a site visit, a dispatch, site work, or a tank drop. */
export const ActivityEntryRow = ({
  entry,
  isSelected = false,
  isActive = false,
  onContextMenu: onContextMenuProp,
  id,
  ...overrides
}: ActivityEntryRowProps) => {
  const {
    timeZone,
    showRevenue = false,
    durationMode = "turnaround",
    showAudit = false,
    auditScoreType = "BUSINESS_VALIDATION_CONFIDENCE",
    onToggleSelected,
    onActivate,
    onEditTransport,
    onOpenAudit,
    onOpenAttachments,
    onEntryContextMenu,
    day,
    idLabel = "resourceId",
  } = useDriverActivityRowSettings(overrides);
  const onContextMenu = onContextMenuProp ?? onEntryContextMenu;
  const isTicket = isActivityTicket(entry);
  const duration = isTicket ? entry.durations[durationMode] : entry.durationMillis;
  const rateModels = entry.rateModels;
  const freight = entry.pickup?.waterTypeName ?? entry.delivery?.waterTypeName;
  const work = entry.pickup?.serviceTypeAbbr ?? entry.pickup?.serviceTypeName ?? entry.delivery?.serviceTypeAbbr ?? entry.delivery?.serviceTypeName;

  return (
    <div
      id={id}
      role="row"
      aria-selected={isTicket ? isSelected : undefined}
      data-active={isActive || undefined}
      data-kind={entry.kind}
      data-service-ticket={entry.isServiceTicket || undefined}
      onClick={() => onActivate?.(entry)}
      onContextMenu={isTicket && onContextMenu ? (event) => {
        event.preventDefault();
        onContextMenu(entry, { x: event.clientX, y: event.clientY });
      } : undefined}
      className={[
        activityRowClass,
        "border-b border-[var(--ots-outline-variant)] text-sm transition-colors last:border-b-0",
        onActivate ? "cursor-pointer" : "",
        isSelected ? "bg-[var(--ots-primary-container)]" : activityTintClass(entry) || "bg-[var(--ots-surface)]",
        isSelected ? "" : "hover:bg-[var(--ots-surface-variant)]",
        activityRailClass(entry),
        isActive ? "relative z-[1] outline outline-2 -outline-offset-2 outline-[var(--ots-primary)]" : "",
        isTicket ? "" : activityMutedClass,
      ].join(" ")}>
      <div role="gridcell" className="flex items-center justify-center">
        {isTicket && onToggleSelected && (
          <OtsCheckbox
            aria-label={`Select ticket ${entry.pickup?.ticketNumber ?? entry.delivery?.ticketNumber ?? ""}`.trim()}
            checked={isSelected}
            onClick={(clickEvent) => {
              clickEvent.stopPropagation();
              onToggleSelected(entry, { range: clickEvent.shiftKey });
            }}
            onChange={() => undefined}/>
        )}
      </div>
      <div role="gridcell" className="flex items-center justify-center"><ActivityStatusIcon entry={entry}/></div>
      <div role="gridcell" className="flex items-center justify-center">
        {showAudit && entry.tta && <AuditScoreRing audit={entry.audit} scoreType={auditScoreType} timeZone={timeZone} onClick={onOpenAudit && (() => onOpenAudit(entry))}/>}
      </div>
      <div role="gridcell" className={[activityCellClass, "!px-1"].join(" ")}>
        {isTicket && (
          <OtsTooltip
            panelClassName="!max-w-none !p-3"
            placement="bottom"
            content={<ActivityEntryPreview entry={entry} timeZone={timeZone} showRevenue={showRevenue} auditScoreType={auditScoreType}/>}>
            <span tabIndex={0} data-resource-id className="truncate rounded font-mono text-[13px] font-semibold text-[var(--ots-primary)] outline-none focus-visible:ring-2 focus-visible:ring-[var(--ots-primary)]">
              {activityIdText(entry, idLabel) ?? "—"}
            </span>
          </OtsTooltip>
        )}
      </div>

      {isTicket ? <>
        <ActivityStopCells
          side="pickup"
          transport={entry.pickup}
          time={entry.tta?.pickupDttm ?? entry.pickup?.createDttm}
          timeZone={timeZone}
          day={day}
          ticketFlag={entry.flags.pickupTicket}
          timeFlag={entry.flags.pickupTime}
          siteFlag={entry.flags.pickupSite}
          qtyFlag={entry.flags.pickupQty}
          markers={stopMarkersFor(entry.pickupMarkers)}
          geofence={entry.pickupGeofence}
          onEdit={onEditTransport && ((change) => onEditTransport(entry, { side: "pickup", ...change }))}/>
        <ActivityStopCells
          side="delivery"
          transport={entry.delivery}
          time={entry.tta?.deliveryDttm ?? entry.delivery?.createDttm}
          timeZone={timeZone}
          day={day}
          ticketFlag={entry.flags.deliveryTicket}
          siteFlag={entry.flags.deliverySite}
          qtyFlag={entry.flags.deliveryQty}
          markers={stopMarkersFor(entry.deliveryMarkers)}
          geofence={entry.deliveryGeofence}
          onEdit={onEditTransport && ((change) => onEditTransport(entry, { side: "delivery", ...change }))}/>
        <div role="gridcell" className={[activityCellClass, activityGroupStartClass].join(" ")}>
          <span className="flex min-w-0 items-center gap-1">
            <OtsTransportCategoryIcon category={entry.category} className="size-3.5 shrink-0"/>
            <OtsFlaggedValue {...entry.flags.freight}>{freight ?? "—"}</OtsFlaggedValue>
          </span>
          <OtsFlaggedValue {...entry.flags.work} className={["text-xs", entry.flags.work ? "" : activityMutedClass].join(" ")}>
            {work ?? "Normal"}
          </OtsFlaggedValue>
        </div>
      </> : (
        <div role="gridcell" className={[activityGroupStartClass, "col-span-7 flex min-w-0 items-center gap-2.5 px-2 py-[var(--da-pad,6px)] text-xs"].join(" ")}>
          <SpanSummary entry={entry} timeZone={timeZone}/>
        </div>
      )}

      <div role="gridcell" className={[activityCellClass, "items-end whitespace-nowrap tabular-nums"].join(" ")}>
        {isTicket && Object.keys(entry.durations).length > 0 ? (
          <OtsTooltip
            density="compact"
            content={<DescriptionList layout="inline" tone="inherit" compact>
              {durationFacts.map(([mode, label]) => entry.durations[mode] != null && (
                <DescriptionItem key={mode} label={label}>{formatActivityDuration(entry.durations[mode])}</DescriptionItem>
              ))}
            </DescriptionList>}>
            <span tabIndex={0}>{formatActivityDuration(duration)}</span>
          </OtsTooltip>
        ) : formatActivityDuration(duration)}
      </div>
      {showRevenue && (
        <div role="gridcell" className={[activityCellClass, "items-end tabular-nums"].join(" ")}>
          {(isTicket || entry.revenue != null) && (rateModels.length > 0 && !entry.flags.revenue ? (
            <OtsTooltip
              density="compact"
              content={<DescriptionList layout="inline" tone="inherit" compact>
                {rateModels.map((model) => <DescriptionItem key={model.id} label={model.rateModelName ?? "Rate model"}>{formatActivityMoney(model.totalCost ?? 0)}</DescriptionItem>)}
              </DescriptionList>}>
              <span tabIndex={0}>{formatActivityMoney(entry.revenue)}</span>
            </OtsTooltip>
          ) : <OtsFlaggedValue {...entry.flags.revenue}>{formatActivityMoney(entry.revenue)}</OtsFlaggedValue>)}
          {!!entry.cost && (
            <OtsTooltip density="compact" content="Driver and trailer cost">
              <span tabIndex={0} className={["text-xs outline-none focus-visible:ring-2 focus-visible:ring-[var(--ots-primary)]", activityMutedClass].join(" ")}>{formatActivityMoney(entry.cost)}</span>
            </OtsTooltip>
          )}
        </div>
      )}
      {/* Validations on the first line, the ticket's side facts on the second, so the column never overflows. */}
      <div role="gridcell" className={[activityCellClass, "gap-1"].join(" ")}>
        <OtsStepDots
          noun="validations"
          steps={entry.validation.map((step) => ({
            id: step.id,
            label: step.label,
            done: step.validatedDttm != null,
            tone: validationTones[step.id],
            detail: step.validatedDttm != null
              ? `${step.validatorName ?? "validated"} · ${formatActivityTime(step.validatedDttm, timeZone)}`
              : "not validated",
          }))}/>
        {((isTicket && entry.markers.length > 0) || entry.attachments.length > 0) && (
          <div className="flex min-w-0 items-center gap-1.5">
            {isTicket && <ActivityMarkerIcons markers={entry.markers} max={MARKERS_PER_LINE}/>}
            <ActivityAttachmentsBadge attachments={entry.attachments} onOpen={onOpenAttachments && (() => onOpenAttachments(entry))}/>
          </div>
        )}
      </div>
    </div>
  );
};
